/**
 * Notifications — a bell panel, not a page.
 *
 * The feed is deliberately ONE FLAT LIST, newest first: the phone's NotifsPage
 * carries the rule in its own words — "no category sections and no per-category
 * mutes… the only switch left is the device-level push subscription". So there
 * is no filter row and no search here either, only the push banner when this
 * device has no subscription, the list, and Load more.
 */
import { useState } from 'react';
import { App as AntApp, Badge, Button, Empty, Flex, Skeleton, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { usePaginatedList } from '../hooks/usePaginatedList';
import { usePushPrompt } from '../context/PushPromptContext';
import { refreshUnreadCount } from '../components/NotifBell';
import { dt } from '../components/kit';
import * as notifsApi from '../api/notifications';

const { Text } = Typography;

// Colour of the leading dot per type — the phone's TYPE_TONE, verbatim.
const TONE = {
  signing: 'var(--accent)', booking: 'var(--accent)', listing: 'var(--accent)',
  co_share: 'var(--purple)',
  payout: 'var(--ok)', earning: 'var(--ok)', commission: 'var(--ok)', paid: 'var(--ok)', approval: 'var(--ok)',
  rejection: 'var(--bad)', voided: 'var(--bad)',
  reminder: 'var(--warn)', expiry: 'var(--warn)',
  system: 'var(--muted)',
};
const toneOf = (t) => TONE[t] || 'var(--primary)';

export default function NotifPanel({ onClose }) {
  const nav = useNavigate();
  const { message } = AntApp.useApp();
  const { supported, subscribed, blocked, enable } = usePushPrompt();
  const [readIds, setReadIds] = useState(() => new Set());
  const list = usePaginatedList(notifsApi.listNotifications, { limit: 10 });

  const isUnread = (n) => !n.read_at && !readIds.has(n.id);
  const hasUnread = list.items.some(isUnread);

  const markAll = async () => {
    try {
      await notifsApi.patchNotificationsReadAll();
      setReadIds((prev) => { const s = new Set(prev); list.items.forEach((n) => s.add(n.id)); return s; });
      refreshUnreadCount();
    } catch (e) { message.error(e.message); }
  };

  const open = async (n) => {
    if (isUnread(n)) {
      try {
        await notifsApi.patchNotificationsReadById(n.id);
        setReadIds((prev) => new Set(prev).add(n.id));
        refreshUnreadCount();
      } catch (e) { message.error(e.message); }
    }
    onClose?.();
    if (n.deep_link) nav(n.deep_link);
  };

  return (
    <div style={{ width: 400 }}>
      <Flex align="center" style={{ padding: '4px 4px 10px' }}>
        <b style={{ fontSize: 13.5 }}>Notifications</b>
        <span style={{ flex: 1 }} />
        {hasUnread && (
          <Button type="text" size="small" style={{ color: 'var(--primary)', fontWeight: 600 }} onClick={markAll}>
            Mark all read
          </Button>)}
      </Flex>

      {/* The one switch the feed still has. When the OS has denied it there is no
          button that can help, so that state explains the only route that works. */}
      {supported && subscribed === false && (blocked ? (
        <Flex align="center" style={{ background: 'color-mix(in srgb,var(--warn) 12%,transparent)',
          border: '1px solid color-mix(in srgb,var(--warn) 30%,transparent)', borderRadius: 10,
          padding: '11px 13px', marginBottom: 10 }}>
          <Text style={{ fontSize: 12, lineHeight: 1.5 }}>
            Notifications are blocked for this app. Allow them in your browser settings, then come back here.
          </Text>
        </Flex>
      ) : (
        <Flex align="center" gap={12} style={{ background: 'var(--chip)', borderRadius: 10, padding: '11px 13px', marginBottom: 10 }}>
          <Text style={{ flex: 1, fontSize: 12, color: 'var(--primary)', fontWeight: 500 }}>
            Enable push notifications to hear about signings &amp; payouts instantly
          </Text>
          <Button size="small" type="primary" onClick={() => enable().then(() => message.success('Push notifications enabled'), (e) => message.error(e.message))}>
            Enable
          </Button>
        </Flex>
      ))}

      {list.loading && !list.items.length ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : list.empty ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }}
          description={<span style={{ fontSize: 12.5 }}>All caught up<br />
            <Text type="secondary" style={{ fontSize: 11.5 }}>New signings, payouts and reminders show up here.</Text></span>} />
      ) : (
        <>
          <Text type="secondary" style={{ fontSize: 11.5, paddingLeft: 4 }}>Newest first</Text>
          <div className="cid-scroll" style={{ maxHeight: 420, overflowY: 'auto', margin: '6px -6px 0' }}>
            {list.items.map((n) => {
              const unread = isUnread(n);
              return (
                <div key={n.id} onClick={() => open(n)}
                  style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '11px 10px',
                    borderRadius: 10, cursor: 'pointer',
                    background: unread ? 'color-mix(in srgb,var(--primary) 5%,transparent)' : 'transparent' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = unread ? 'color-mix(in srgb,var(--primary) 5%,transparent)' : 'transparent'; }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', flex: 'none', marginTop: 5, background: toneOf(n.type) }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: unread ? 700 : 500, lineHeight: 1.4 }}>{n.title}</div>
                    {n.body && <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>{n.body}</div>}
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{dt(n.at)}</div>
                  </div>
                  {unread && <Badge status="processing" style={{ marginTop: 4 }} />}
                </div>);
            })}
          </div>
          {list.hasMore && (
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, textAlign: 'center' }}>
              <Button type="text" size="small" loading={list.loadingMore} onClick={list.loadMore}>Load more</Button>
            </div>)}
        </>
      )}
    </div>
  );
}
